type StructuredData = Record<string, unknown>

const PUBLISHER_NAME = 'Egebit Limited'
const PUBLISHER_LEGAL_NAME = 'Egebit Elektronik Hizmetleri ve İnşaat Ticaret Limited Şirketi'
const PUBLISHER_URL = 'https://egebit.com/'
const APP_STORE_URL = 'https://apps.apple.com/app/id6747636443'
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.salestaxcalculator'

type BreadcrumbItem = {
  name: string
  path: string
}

type StaticPageInput = {
  route: string
  title: string
  description: string
}

type ContentAuthor = {
  slug: string
  name: string
  role: string
  image: string
  shortBio?: string
  expertise?: string[]
}

type ArticleInput = {
  kind: 'article' | 'guide'
  slug: string
  title: string
  description: string
  image: string
  imageAlt?: string
  publishedOn?: string
  reviewedOn?: string
  section: string
  keywords: string[]
  wordCount: number
  readingMinutes: number
  author: ContentAuthor
}

type ProfileContent = {
  kind: 'article' | 'guide'
  slug: string
  title: string
  publishedOn?: string
}

type ProfileInput = {
  author: ContentAuthor
  content: ProfileContent[]
}

const context = 'https://schema.org'

function absolute(origin: string, path: string) {
  const normalizedOrigin = origin.replace(/\/$/, '')
  return `${normalizedOrigin}${path === '/' ? '/' : path}`
}

function organization(origin: string): StructuredData {
  const home = absolute(origin, '/')
  return {
    '@type': 'Organization',
    '@id': `${home}#organization`,
    name: PUBLISHER_NAME,
    legalName: PUBLISHER_LEGAL_NAME,
    url: PUBLISHER_URL,
    description: 'The company that publishes Sales Tax Kit and the Sales Tax Calculator mobile applications.',
    logo: {
      '@type': 'ImageObject',
      '@id': `${home}#logo`,
      url: absolute(origin, '/assets/icon-1024.png'),
      contentUrl: absolute(origin, '/assets/icon-1024.png'),
      width: 1024,
      height: 1024,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: absolute(origin, '/contact'),
      availableLanguage: 'English',
    },
    brand: {
      '@type': 'Brand',
      '@id': `${home}#brand`,
      name: 'Sales Tax Kit',
      url: home,
    },
  }
}

function editorialTeam(origin: string): StructuredData {
  const home = absolute(origin, '/')
  return {
    '@type': 'Organization',
    '@id': `${home}#editorial-team`,
    name: 'Sales Tax Kit Editorial Team',
    url: absolute(origin, '/about'),
    parentOrganization: { '@id': `${home}#organization` },
  }
}

function mobileApplications(origin: string): StructuredData[] {
  const publisher = { '@id': `${absolute(origin, '/')}#organization` }
  const common = {
    '@type': 'MobileApplication',
    name: 'Sales Tax Calculator',
    applicationCategory: 'FinanceApplication',
    isPartOf: { '@id': `${absolute(origin, '/')}#brand` },
    publisher,
  }
  return [
    { ...common, '@id': `${absolute(origin, '/')}#ios-app`, operatingSystem: 'iOS', downloadUrl: APP_STORE_URL, installUrl: APP_STORE_URL },
    { ...common, '@id': `${absolute(origin, '/')}#android-app`, operatingSystem: 'Android', downloadUrl: GOOGLE_PLAY_URL, installUrl: GOOGLE_PLAY_URL },
  ]
}

function website(origin: string): StructuredData {
  const home = absolute(origin, '/')
  return {
    '@type': 'WebSite',
    '@id': `${home}#website`,
    name: 'Sales Tax Kit',
    url: home,
    publisher: { '@id': `${home}#organization` },
    inLanguage: 'en-US',
  }
}

function breadcrumb(origin: string, items: BreadcrumbItem[]): StructuredData {
  return {
    '@type': 'BreadcrumbList',
    '@id': `${absolute(origin, items.at(-1)?.path || '/')}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absolute(origin, item.path),
    })),
  }
}

function pageType(route: string) {
  if (route === '/guides' || route === '/blog' || route === '/tools') return 'CollectionPage'
  if (route === '/contact') return 'ContactPage'
  return 'WebPage'
}

function staticBreadcrumbs(route: string, title: string): BreadcrumbItem[] {
  if (route.startsWith('/tools/')) {
    return [{ name: 'Home', path: '/' }, { name: 'Tools', path: '/tools' }, { name: title, path: route }]
  }
  return [{ name: 'Home', path: '/' }, { name: title, path: route }]
}

export function homeStructuredData(origin: string): StructuredData {
  const home = absolute(origin, '/')
  return {
    '@context': context,
    '@graph': [
      organization(origin),
      website(origin),
      editorialTeam(origin),
      ...mobileApplications(origin),
      {
        '@type': 'WebApplication',
        '@id': `${home}#web-calculator`,
        name: 'Sales Tax Kit Calculator',
        url: absolute(origin, '/calculator'),
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'Any',
        browserRequirements: 'Requires JavaScript',
        isAccessibleForFree: true,
        publisher: { '@id': `${home}#organization` },
      },
      {
        '@type': 'WebPage',
        '@id': home,
        url: home,
        name: 'Free Sales Tax Calculator, VAT & GST Tools | Sales Tax Kit',
        description: 'Estimate sales tax, VAT, and GST with free browser tools. Compare locations, reverse tax, and explore official-source guides.',
        isPartOf: { '@id': `${home}#website` },
        about: { '@id': `${home}#brand` },
        inLanguage: 'en-US',
      },
    ],
  }
}

export function staticPageStructuredData(origin: string, page: StaticPageInput): StructuredData {
  if (page.route === '/') return homeStructuredData(origin)
  const canonical = absolute(origin, page.route)
  return {
    '@context': context,
    '@graph': [
      organization(origin),
      website(origin),
      editorialTeam(origin),
      {
        '@type': pageType(page.route),
        '@id': canonical,
        url: canonical,
        name: page.title,
        description: page.description,
        isPartOf: { '@id': `${absolute(origin, '/')}#website` },
        publisher: { '@id': `${absolute(origin, '/')}#organization` },
        breadcrumb: { '@id': `${canonical}#breadcrumb` },
        inLanguage: 'en-US',
      },
      breadcrumb(origin, staticBreadcrumbs(page.route, page.title)),
    ],
  }
}

export function articleStructuredData(origin: string, input: ArticleInput): StructuredData {
  const sectionPath = input.kind === 'article' ? '/blog' : '/guides'
  const sectionName = input.kind === 'article' ? 'Blog' : 'Tax Guides'
  const canonical = absolute(origin, `${sectionPath}/${input.slug}`)
  const authorUrl = absolute(origin, `/authors/${input.author.slug}`)
  const image = absolute(origin, input.image)
  const imageObject = {
    '@type': 'ImageObject',
    '@id': `${canonical}#primary-image`,
    url: image,
    contentUrl: image,
    width: 1600,
    height: 900,
    caption: input.imageAlt || `${input.title} ${input.kind === 'article' ? 'article' : 'guide'} cover`,
  }
  return {
    '@context': context,
    '@graph': [
      organization(origin),
      website(origin),
      editorialTeam(origin),
      {
        '@type': input.kind === 'article' ? 'BlogPosting' : 'Article',
        '@id': `${canonical}#article`,
        headline: input.title,
        description: input.description,
        image: [imageObject],
        datePublished: input.publishedOn || '2026-08-08',
        dateModified: input.reviewedOn || input.publishedOn || '2026-08-08',
        inLanguage: 'en-US',
        wordCount: input.wordCount,
        timeRequired: `PT${input.readingMinutes}M`,
        articleSection: input.section,
        keywords: input.keywords,
        isAccessibleForFree: true,
        author: {
          '@type': 'Organization',
          '@id': `${authorUrl}#editorial-desk`,
          name: input.author.name,
          description: input.author.shortBio,
          url: authorUrl,
          parentOrganization: { '@id': `${absolute(origin, '/')}#editorial-team` },
        },
        publisher: { '@id': `${absolute(origin, '/')}#organization` },
        mainEntityOfPage: { '@id': canonical },
        url: canonical,
      },
      {
        '@type': 'WebPage',
        '@id': canonical,
        url: canonical,
        name: input.title,
        description: input.description,
        isPartOf: { '@id': `${absolute(origin, '/')}#website` },
        about: { '@id': `${absolute(origin, '/')}#organization` },
        breadcrumb: { '@id': `${canonical}#breadcrumb` },
        primaryImageOfPage: { '@id': `${canonical}#primary-image` },
        inLanguage: 'en-US',
      },
      breadcrumb(origin, [
        { name: 'Home', path: '/' },
        { name: sectionName, path: sectionPath },
        { name: input.title, path: `${sectionPath}/${input.slug}` },
      ]),
    ],
  }
}

export function profileStructuredData(origin: string, input: ProfileInput): StructuredData {
  const canonical = absolute(origin, `/authors/${input.author.slug}`)
  const deskId = `${canonical}#editorial-desk`
  return {
    '@context': context,
    '@graph': [
      organization(origin),
      website(origin),
      editorialTeam(origin),
      {
        '@type': 'ProfilePage',
        '@id': canonical,
        url: canonical,
        name: `${input.author.name} — Sales Tax Kit`,
        description: input.author.shortBio,
        isPartOf: { '@id': `${absolute(origin, '/')}#website` },
        breadcrumb: { '@id': `${canonical}#breadcrumb` },
        mainEntity: { '@id': deskId },
        hasPart: input.content.map((item) => ({
          '@type': item.kind === 'article' ? 'BlogPosting' : 'Article',
          headline: item.title,
          url: absolute(origin, `/${item.kind === 'article' ? 'blog' : 'guides'}/${item.slug}`),
          datePublished: item.publishedOn || '2026-08-08',
          author: { '@id': deskId },
        })),
        inLanguage: 'en-US',
      },
      {
        '@type': 'Organization',
        '@id': deskId,
        identifier: input.author.slug,
        name: input.author.name,
        description: input.author.shortBio,
        logo: {
          '@type': 'ImageObject',
          url: absolute(origin, input.author.image),
          contentUrl: absolute(origin, input.author.image),
          width: 1024,
          height: 1024,
        },
        url: canonical,
        parentOrganization: { '@id': `${absolute(origin, '/')}#editorial-team` },
        knowsAbout: input.author.expertise,
      },
      breadcrumb(origin, [
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' },
        { name: input.author.name, path: `/authors/${input.author.slug}` },
      ]),
    ],
  }
}
